import React, { useState, useRef } from 'react';
import IngredientTooltip from './IngredientTooltip';

const MealplanIngredientsManager = ({ ingredients, shoppingList, onToggleItem, onPrintShoppingList, onPrintIngredientList, onSelectAll, onClearShoppingList }) => {
  const [hoveredIngredient, setHoveredIngredient] = useState(null);
  const [hoveredShoppingListItem, setHoveredShoppingListItem] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, ingredient: null, target: null, triggeredBy: null });
  const tooltipTimer = useRef(null);

  const sortedIngredients = [...ingredients].sort((a, b) => a.name.localeCompare(b.name));

  const isSelected = (ingredient) => {
    return shoppingList.some(item => item.name === ingredient.name && item.unit === ingredient.unit);
  };

  const shoppingListWithData = shoppingList
    .map(key => ingredients.find(i => i.name === key.name && i.unit === key.unit))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleMouseEnter = (ingredient, e) => {
    if (window.innerWidth < 768) return;
    const target = e.currentTarget;
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    setHoveredIngredient(ingredient);
    tooltipTimer.current = setTimeout(() => {
      setTooltip({
        visible: true,
        ingredient,
        target,
        triggeredBy: 'hover'
      });
    }, 500);
  };

  const handleMouseLeave = () => {
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    setHoveredIngredient(null);
    if (tooltip.triggeredBy === 'hover') {
      setTooltip({ visible: false, ingredient: null, target: null, triggeredBy: null });
    }
  };

  return (
    <>
      {tooltip.visible && (
        <IngredientTooltip
          ingredient={tooltip.ingredient}
          target={tooltip.target}
          mobileMode={tooltip.triggeredBy === 'click'}
          onClose={() => setTooltip({ visible: false, ingredient: null, target: null, triggeredBy: null })}
        />
      )}
      {ingredients.length > 0 && (
        <div className="ingredients-container">
          <div className="ingredient-list-panel section" data-tour="meal-plan-ingredients-list">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2><i className="material-icons" style={{ marginRight: '5px' }}>kitchen</i>Ingredient List</h2>
              {ingredients.length > shoppingList.length && (
                <div data-tour="select-all-ingredients-button" onClick={onSelectAll} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} title="Add all ingredients to shopping list">
                  <i className="material-icons">arrow_forward</i>
                  <i className="material-icons">shopping_cart</i>
                </div>
              )}
            </div>
            <ul style={{ flexGrow: 1, marginTop: '0.5rem' }}>
              {sortedIngredients.map((ingredient) => (
                <li key={`${ingredient.name}-${ingredient.unit}`}
                  onClick={() => onToggleItem(ingredient)}
                  className={`ingredient-item ${isSelected(ingredient) ? 'selected' : ''}`}
                  onMouseEnter={(e) => handleMouseEnter(ingredient, e)}
                  onMouseLeave={handleMouseLeave}>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      className="icon-button mobile-view-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (tooltip.visible && tooltip.ingredient && tooltip.ingredient.name === ingredient.name && tooltip.ingredient.unit === ingredient.unit && tooltip.triggeredBy === 'click') {
                          setTooltip({ visible: false, ingredient: null, target: null, triggeredBy: null });
                        } else {
                          setTooltip({ visible: true, ingredient, target: e.currentTarget, triggeredBy: 'click' });
                        }
                      }}
                      title="View details"
                      style={{ marginRight: '5px' }}
                    >
                      <i className="material-icons">visibility</i>
                    </button>
                    {ingredient.name}
                    {ingredient.amount > 0 && ` ${ingredient.amount}`}
                    {ingredient.amount > 0 && ingredient.unit && ` ${ingredient.unit}`}
                  </span>
                  <div style={{ marginLeft: 'auto', minHeight: '24px', minWidth: '48px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isSelected(ingredient) ? (
                      hoveredIngredient && hoveredIngredient.name === ingredient.name && hoveredIngredient.unit === ingredient.unit ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <i className="material-icons">arrow_back</i>
                          <i className="material-icons">shopping_cart</i>
                        </div>
                      ) : (
                        <i className="material-icons">shopping_cart</i>
                      )
                    ) : (
                      hoveredIngredient && hoveredIngredient.name === ingredient.name && hoveredIngredient.unit === ingredient.unit && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <i className="material-icons">arrow_forward</i>
                          <i className="material-icons">shopping_cart</i>
                        </div>
                      )
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="save-buttons-container" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button title="Print ingredient list" onClick={onPrintIngredientList} className="print-button">
                <i className="material-icons">print</i> Print
              </button>
            </div>
          </div>
          <div className="shopping-list-panel section" data-tour="shopping-list-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Shopping List</h2>
              {shoppingListWithData.length > 0 && (
                <div onClick={onClearShoppingList} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} title="Remove all items from shopping list">
                  <i className="material-icons">arrow_back</i>
                  <i className="material-icons">shopping_cart</i>
                </div>
              )}
            </div>
            {shoppingListWithData.length > 0 ? (
              <ul style={{ flexGrow: 1 }}>
                {shoppingListWithData.map((ingredient) => (
                  <li key={`${ingredient.name}-${ingredient.unit}`}
                    onClick={() => onToggleItem(ingredient)}
                    className="ingredient-item"
                    onMouseEnter={() => setHoveredShoppingListItem(ingredient)}
                    onMouseLeave={() => setHoveredShoppingListItem(null)}>
                    <span>
                      {ingredient.name}
                      {ingredient.amount > 0 && ` ${ingredient.amount}`}
                      {ingredient.amount > 0 && ingredient.unit && ` ${ingredient.unit}`}
                    </span>
                    <div style={{ marginLeft: 'auto', minHeight: '24px', minWidth: '48px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      {hoveredShoppingListItem && hoveredShoppingListItem.name === ingredient.name && hoveredShoppingListItem.unit === ingredient.unit && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <i className="material-icons">arrow_back</i>
                          <i className="material-icons">shopping_cart</i>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ flexGrow: 1 }}>Select ingredients to add them to your shopping list.</p>
            )}
            {shoppingListWithData.length > 0 && (
              <div className="save-buttons-container" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }} data-tour="shopping-list-print-button">
                <button title="Print shopping list" onClick={onPrintShoppingList} className="print-button">
                  <i className="material-icons">print</i> Print
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MealplanIngredientsManager;